<template>
    <nav role="navigation">
        <div class="fixed top-0 z-30 h-20 w-full border-b border-nomad-800 bg-nomad-900 text-white shadow-md">
            <div class="relative mx-auto flex h-full max-w-screen-2xl items-center justify-between px-4 sm:px-6">
                <div class="flex flex-1 justify-start">
                    <button
                        type="button"
                        @click="toggleMenu"
                        class="flex h-12 w-12 items-center justify-center rounded-xl text-2xl text-nomad-200 transition-colors hover:bg-nomad-800 hover:text-nomad-accent focus:outline-none focus:ring-2 focus:ring-nomad-accent"
                        aria-label="Open menu"
                        aria-controls="site-navigation-menu"
                        :aria-expanded="isOpen"
                    >
                        <span class="icon-menu" aria-hidden="true"></span>
                    </button>
                </div>
                <Link href="/" class="absolute left-1/2 -translate-x-1/2" aria-label="NomadLyrics home">
                    <img src="@/assets/logo-full.svg" alt="NomadLyrics" class="h-11 select-none sm:h-12" />
                </Link>
                <div class="flex flex-1 justify-end">
                    <Link
                        href="/search"
                        class="flex h-12 w-12 items-center justify-center rounded-xl text-2xl text-nomad-200 transition-colors hover:bg-nomad-800 hover:text-nomad-accent focus:outline-none focus:ring-2 focus:ring-nomad-accent"
                        aria-label="Site search"
                    >
                        <span class="icon-search" aria-hidden="true"></span>
                    </Link>
                </div>
            </div>
        </div>
        <div
            id="site-navigation-menu"
            class="fixed inset-0 z-50 flex bg-black/70 backdrop-blur-md transition-opacity duration-300"
            :class="isOpen ? 'pointer-events-auto opacity-100' : 'pointer-events-none opacity-0'"
            @click.self="toggleMenu"
        >
            <aside
                class="navigation-scrollbar my-4 flex h-[calc(100%-2rem)] w-[85vw] max-w-sm shrink-0 flex-col overflow-y-auto rounded-r-3xl bg-nomad-900 shadow-2xl transition-transform duration-300 ease-out"
                :class="isOpen ? 'translate-x-0' : '-translate-x-full'"
                :inert="!isOpen"
                aria-label="Main menu"
            >
                <div class="flex items-center justify-between border-b border-white/5 px-6 py-6">
                    <Link href="/" aria-label="NomadLyrics home" @click="toggleMenu">
                        <img src="@/assets/logo-full.svg" alt="NomadLyrics" class="h-10 select-none" />
                    </Link>
                    <button
                        type="button"
                        @click="toggleMenu"
                        class="flex h-11 w-11 items-center justify-center rounded-xl text-2xl text-nomad-300 transition-colors hover:bg-white/5 hover:text-white focus:outline-none focus:ring-2 focus:ring-nomad-accent"
                        aria-label="Close menu"
                    >
                        <span class="icon-x" aria-hidden="true"></span>
                    </button>
                </div>

                <div v-if="auth && auth.id" class="mx-5 mt-5 flex items-center gap-4 rounded-2xl border border-white/5 bg-nomad-800 p-4">
                    <img :src="auth.avatar" alt="Profile picture" class="h-14 w-14 rounded-full border border-white/10 object-cover" />
                    <div class="min-w-0">
                        <h3 class="truncate text-base font-semibold text-white">{{ auth.name }}</h3>
                        <span class="text-sm text-nomad-300">Member since {{ memberSinceYear(auth.created_at) }}</span>
                    </div>
                </div>

                <div class="px-6 pb-3 pt-7 text-xs font-semibold uppercase tracking-[0.18em] text-nomad-400">
                    Explore
                </div>
                <ul class="grid gap-1.5 px-4 pb-6 text-lg">
                    <li v-for="menu in visibleMenus" :key="menu.permlink">
                        <Link
                            v-if="!menu.target"
                            :href="'/' + menu.permlink"
                            class="group relative flex min-h-14 items-center gap-4 rounded-xl px-4 py-3 text-nomad-200 transition-all duration-200 hover:bg-nomad-800 hover:text-white"
                            :class="{ 'bg-nomad-accent/10 text-nomad-accent': $page.url.startsWith('/' + menu.permlink) }"
                            @click="toggleMenu"
                        >
                            <span class="w-6 text-center text-lg opacity-80 transition-opacity group-hover:opacity-100" :class="menu.icon" aria-hidden="true"></span>
                            <span class="flex-1">{{ menu.title }}</span>
                        </Link>
                        <a
                            v-else
                            :href="menu.permlink"
                            target="_blank"
                            rel="noopener noreferrer"
                            class="flex min-h-14 items-center gap-4 rounded-xl px-4 py-3 text-lg text-nomad-200 transition-colors hover:bg-nomad-800 hover:text-white"
                        >
                            <span class="w-5 text-center" :class="menu.icon" aria-hidden="true"></span>
                            <span>{{ menu.title }}</span>
                            <span class="icon-external-link ml-auto text-sm text-nomad-500" aria-hidden="true"></span>
                        </a>
                    </li>
                </ul>

                <div class="mt-auto grid gap-3 border-t border-white/5 p-5">
                    <a
                        v-if="isLoggedIn"
                        :href="'https://pay.rev.cat/qctnwhyrublbpkwd/' + auth.firebase_uid"
                        target="_blank"
                        rel="noopener noreferrer"
                        class="flex min-h-14 items-center gap-4 rounded-xl bg-nomad-accent px-5 py-3 text-base font-semibold text-black transition-colors hover:bg-nomad-accent/90"
                    >
                        <span class="icon-credit-card" aria-hidden="true"></span>
                        <span>Buy Subscription</span>
                        <span class="icon-external-link ml-auto text-sm" aria-hidden="true"></span>
                    </a>
                    <a
                        v-if="!isLoggedIn"
                        href="/auth/google"
                        class="flex min-h-14 items-center gap-4 rounded-xl bg-nomad-accent px-5 py-3 text-base font-semibold text-black transition-colors hover:bg-nomad-accent/90"
                    >
                        <span class="icon-user" aria-hidden="true"></span>
                        <span>Login</span>
                    </a>
                    <a
                        v-else
                        href="/auth/google"
                        @click.prevent="logoutUser"
                        class="flex min-h-14 items-center gap-4 rounded-xl bg-nomad-800 px-5 py-3 text-base font-medium text-nomad-200 transition-colors hover:bg-nomad-700 hover:text-white"
                    >
                        <span class="icon-user-x" aria-hidden="true"></span>
                        <span>Logout</span>
                    </a>
                    <Link
                        v-if="auth && auth.id && auth.isAdmin"
                        href="/backstage"
                        class="flex min-h-14 items-center gap-4 rounded-xl border border-white/5 bg-nomad-800 px-5 py-3 text-base font-medium text-nomad-200 transition-colors hover:bg-nomad-700 hover:text-nomad-accent"
                        @click="toggleMenu"
                    >
                        <span class="icon-settings" aria-hidden="true"></span>
                        <span>Backstage</span>
                    </Link>
                </div>
            </aside>
        </div>
    </nav>
</template>
<script>
import { Link } from "@inertiajs/vue3";
import { mapState, mapActions } from "vuex";
export default {
    data() {
        return {
            isOpen: false,
            menus: [
                { permlink: "search", title: "Search", icon: "icon-search" },
                { permlink: "just-added", title: "Just added", icon: "icon-music" },
                { permlink: "playlists", title: "Playlists", icon: "icon-bar-chart-2" },
                { permlink: "podcasts", title: "Podcasts", icon: "icon-mic" },
                { permlink: "smart", title: "Smart Music", icon: "icon-sliders" },
                { permlink: "favorites", title: "Likes", icon: "icon-heart" },
                { permlink: "stories", title: "Local News", icon: "icon-circle text-nomad-red" },
            ],
        };
    },
    computed: {
        ...mapState({
            auth: (state) => state.user.auth,
            isLoggedIn: (state) => state.user.isLoggedIn,
        }),
        visibleMenus() {
            return this.menus.filter((menu) => menu.permlink !== "favorites" || this.isLoggedIn);
        },
    },
    methods: {
        ...mapActions("user", ["logoutUser"]),
        toggleMenu() {
            this.isOpen = !this.isOpen;
        },
        memberSinceYear(createdAt) {
            if (!createdAt) return "";

            const date = new Date(createdAt);
            return Number.isNaN(date.getTime()) ? "" : date.getFullYear();
        },
    },
    components: { Link },
};
</script>
<style scoped>
.navigation-scrollbar {
    scrollbar-color: #343630 transparent;
    scrollbar-width: thin;
}

.navigation-scrollbar::-webkit-scrollbar {
    width: 5px;
}

.navigation-scrollbar::-webkit-scrollbar-track {
    background: transparent;
}

.navigation-scrollbar::-webkit-scrollbar-thumb {
    border: 0;
    border-radius: 9999px;
    background: #343630;
}

.navigation-scrollbar::-webkit-scrollbar-thumb:hover {
    background: #494c43;
}
</style>
